/**
 * Crisp signals — behaviour the agent can act on, written to the visitor's
 * Crisp session. First (and so far only) signal: add-to-cart.
 * ─────────────────────────────────────────────────────────────────────────────
 * On a successful add to cart this pushes, in this order:
 *
 *   session:event    added_to_cart {product, variant_id, qty, price}  (green)
 *     → the thing dashboard Triggers can fire on ("On user event")
 *   session:segments ["added_to_cart"]
 *     → shows on the visitor/conversation profile
 *   session:data     last_added, cart_total, cart_items
 *     → cart_total/cart_items read back from /cart.js AFTER the add
 *   user:nickname    "Cart Shopper" for a guest, once per session
 *     → the live Visitors list has no segment filter (checked, 2026-09-02),
 *       so the cart marker rides on the name, which that list does show
 *
 * Second job: the signed-in customer. When snippets/modern-crisp.liquid
 * renders a customer block, their name, email and order history go onto the
 * Crisp profile at page load, so the agent sees a person rather than an
 * anonymous visitor. None of it is verified — the shopper's own browser can
 * overwrite any of it — so it is context, never proof of identity.
 *
 * Third job: the cart alert. MB_CRISP_SIG_SCENARIO is a Crisp Workflow that
 * posts a private note and mentions the operator — nothing the shopper sees.
 * It runs on the add to cart itself, once per MB_CRISP_SIG_ALERT_COOLDOWN_MS,
 * and never for a visitor located in Turkey.
 *
 * Dashboard Triggers can't do this job: they fire once per visitor for life,
 * with no cooldown or repeat setting (checked 2026-09-02).
 *
 * ATC pipeline (CLAUDE.md §6): this file only LISTENS — to the bubbling
 * `product-form:added` event that modern-product-form.js dispatches after
 * /cart/add.js succeeds. It never touches the form, the request, or the
 * response handling, and a failure here can't reach the pipeline (every
 * handler body is guarded).
 *
 * Timing (measured 2026-09-02): `set` pushes sent right after $crisp gets its
 * methods were silently dropped; the same pushes stuck once the session had
 * loaded. So every push waits for the session — the `mb-crisp:session` relay
 * from modern-crisp-quiet.js (the single Crisp `on` subscriber), with a
 * session:identifier poll as the late-loader fallback.
 *
 * Add ?mbcrispdebug=1 to any URL to log every push to the console.
 */

const MB_CRISP_SIG_SESSION_TIMEOUT_MS = 30000;
const MB_CRISP_SIG_POLL_MS = 500;

// Crisp Workflow "cart-alert" (AI Agent → Workflows). Started programmatically
// from here, so it must begin with an action block, not a message event.
const MB_CRISP_SIG_SCENARIO = 'scenario_f9e32beb-0301-4403-a3b6-33d79fa2b9c5';
// Crisp's own "Stop Workflow" guard does not hold against a programmatic
// bot:scenario:run, so the gap between alerts is enforced here.
const MB_CRISP_SIG_ALERT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
// Minimum cart value worth an agent's attention, read from the `cart_total` we
// wrote ourselves. 0 alerts on every cart; set it to e.g. 1000 to filter.
const MB_CRISP_SIG_CART_MIN = 0;
// No alert for visitors whose IP is in Turkey, per Shopify's geolocation.
const MB_CRISP_SIG_SKIP_COUNTRY = 'TR';

const mbCrispSigDebug = new URLSearchParams(location.search).has('mbcrispdebug');
const mbCrispSigLog = (...args) => {
  if (mbCrispSigDebug) console.log('%c[mb-crisp-signals]', 'color:#7a5c00;font-weight:700', ...args);
};

const mbCrispSigPush = (...command) => {
  try {
    window.$crisp.push(command);
    mbCrispSigLog('push:', ...command);
  } catch (error) {
    mbCrispSigLog('push failed:', command, error.message);
  }
};

/**
 * Crisp's local copy of session data. It lags a `set` push by a moment (the
 * value round-trips), so never read back something written milliseconds ago.
 */
const mbCrispSigData = () => {
  try {
    return window.$crisp.get('session:data') ?? {};
  } catch {
    return {};
  }
};

/**
 * Resolves once the Crisp session is actually up (not merely `$crisp` having
 * its methods — see the timing note in the header). Late-safe: if the
 * session loaded before this file, the identifier poll answers instead of
 * the already-fired relay event.
 */
const mbCrispSigSessionReady = new Promise((resolve) => {
  let settled = false;
  const settle = (how) => {
    if (settled) return;
    settled = true;
    mbCrispSigLog('session ready via', how);
    resolve();
  };

  document.addEventListener('mb-crisp:session', () => settle('mb-crisp:session'), { once: true });

  const startedAt = Date.now();
  const tick = () => {
    if (settled) return;
    try {
      if (window.$crisp?.get?.('session:identifier')) {
        settle('identifier poll');
        return;
      }
    } catch {
      /* not up yet */
    }
    if (Date.now() - startedAt > MB_CRISP_SIG_SESSION_TIMEOUT_MS) {
      mbCrispSigLog('gave up — no Crisp session');
      return;
    }
    setTimeout(tick, MB_CRISP_SIG_POLL_MS);
  };
  tick();
});

/** The signed-in customer, or null for a guest. */
const mbCrispSigCustomer = () => {
  const el = document.getElementById('mb-crisp-customer');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    mbCrispSigLog('customer block unreadable:', error.message);
    return null;
  }
};

const mbCrispSigUser = (field) => {
  try {
    return window.$crisp.get(`user:${field}`) ?? null;
  } catch {
    return null;
  }
};

/**
 * Puts the signed-in customer on the Crisp profile. Runs on every pageview but
 * only writes what Crisp does not already hold, so a shopper browsing twenty
 * pages costs one write, not twenty — and an order placed mid-session still
 * updates the count on the next page.
 *
 * The nickname is left alone once an add to cart has marked it: that marker is
 * what the Visitors list is read by, and it must not be overwritten.
 */
const mbCrispSigIdentify = async () => {
  const customer = mbCrispSigCustomer();
  if (!customer) return;

  await mbCrispSigSessionReady;

  if (customer.email && mbCrispSigUser('email') !== customer.email) {
    mbCrispSigPush('set', 'user:email', [customer.email]);
  }

  const data = mbCrispSigData();
  if (customer.name && mbCrispSigUser('nickname') !== customer.name) {
    mbCrispSigPush('set', 'user:nickname', [customer.name]);
  }

  const fields = [];
  const orders = Number(customer.orders) || 0;
  if (customer.orders != null && mbCrispSigNumber(data.customer_orders) !== orders) {
    fields.push(['customer_orders', orders]);
  }
  if (customer.spent && String(data.customer_spent ?? '') !== String(customer.spent)) {
    fields.push(['customer_spent', String(customer.spent)]);
  }
  if (fields.length) mbCrispSigPush('set', 'session:data', [fields]);
};

const mbCrispSigMoney = (cents) => `$${(cents / 100).toFixed(2)}`;

const mbCrispSigNumber = (value) => Number(value) || 0;

/** "$1660.50" → 1660.5. Anything unparseable reads as 0, i.e. no alert. */
const mbCrispSigCartValue = (total) => mbCrispSigNumber(String(total ?? '').replace(/[^0-9.]/g, ''));

/**
 * True when the visitor is in Turkey. Looked up once per pageview, on the
 * first add to cart. A failed lookup reads as "not Turkey": a missed skip
 * costs one note, a false skip loses a real alert.
 */
let mbCrispSigInSkippedCountry;
const mbCrispSigSkipLocation = () =>
  (mbCrispSigInSkippedCountry ??= (async () => {
    try {
      const res = await fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return json?.detected_values?.country?.handle === MB_CRISP_SIG_SKIP_COUNTRY;
    } catch (error) {
      mbCrispSigLog('location lookup failed:', error.message);
      return false;
    }
  })());

/**
 * Runs the alert workflow, unless it already ran inside the cooldown. The
 * cooldown is re-read here rather than trusted from the caller: a second tab
 * may have fired in the meantime, and session data is shared between them.
 */
/**
 * `cart` carries the values the caller has just measured. They are NOT read
 * back from session data: that copy lags a write, and reading it here sent the
 * workflow an empty product and total — the note arrived as "Cart —".
 */
const mbCrispSigFireCartAlert = async (cart = {}) => {
  // Awaited before the cooldown read so the read-and-stamp below stays one
  // synchronous step.
  if (await mbCrispSigSkipLocation()) {
    mbCrispSigLog('alert skipped — visitor in', MB_CRISP_SIG_SKIP_COUNTRY);
    return;
  }

  const data = mbCrispSigData();
  const product = cart.product ?? String(data.last_added ?? '');
  const total = cart.total ?? String(data.cart_total ?? '');
  const items = cart.items ?? String(data.cart_items ?? '');

  if (Date.now() - mbCrispSigNumber(data.cart_alert_at) < MB_CRISP_SIG_ALERT_COOLDOWN_MS) {
    mbCrispSigLog('alert skipped — inside cooldown');
    return;
  }

  const value = mbCrispSigCartValue(total);
  if (MB_CRISP_SIG_CART_MIN > 0 && value < MB_CRISP_SIG_CART_MIN) {
    mbCrispSigLog('alert skipped — cart under threshold:', value);
    return;
  }

  // Stamped before the run: the workflow may answer instantly, and a second
  // add landing in that window must already see the alert as spent.
  mbCrispSigPush('set', 'session:data', [[['cart_alert_at', Date.now()]]]);
  mbCrispSigPush('do', 'bot:scenario:run', [
    MB_CRISP_SIG_SCENARIO,
    { product: String(product), cart_total: String(total), cart_items: String(items), page: location.pathname },
  ]);
};

const mbCrispSigOnAdded = async (detail) => {
  // add.js was posted as {items:[...]}, so the response mirrors that shape;
  // a lone object is kept as a fallback against upstream format changes.
  const response = detail?.response ?? {};
  const items = Array.isArray(response.items) ? response.items : [response];
  const first = items[0] ?? {};
  const qty = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const product = [first.product_title, first.variant_title].filter(Boolean).join(' — ');

  await mbCrispSigSessionReady;

  mbCrispSigPush('set', 'session:event', [[[
    'added_to_cart',
    {
      product: product || 'unknown',
      variant_id: first.variant_id ?? detail?.variantId ?? null,
      qty,
      price: typeof first.final_price === 'number' ? mbCrispSigMoney(first.final_price) : null,
    },
    'green',
  ]]]);
  mbCrispSigPush('set', 'session:segments', [['added_to_cart']]);

  const fields = [];
  if (product) fields.push(['last_added', product]);

  // The live Visitors list shows names, not segments, so a guest with a cart
  // is marked through the only field that list renders. A signed-in shopper
  // keeps their own name untouched — it is their name, not a status field.
  if (!mbCrispSigCustomer() && !mbCrispSigData().cart_marked) {
    mbCrispSigPush('set', 'user:nickname', ['Cart Shopper']);
    fields.push(['cart_marked', true]);
  }

  if (fields.length) mbCrispSigPush('set', 'session:data', [fields]);

  // Totals come from /cart.js, not from the add response (which only has the
  // added lines). A side read: its failure must never surface anywhere near
  // the ATC pipeline, so it only logs.
  let total = '';
  let itemCount = '';
  try {
    const cartUrl = `${window.routes?.cart_url ?? '/cart'}.js`;
    const res = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const cart = await res.json();
    total = mbCrispSigMoney(cart.total_price);
    itemCount = String(cart.item_count);
    mbCrispSigPush('set', 'session:data', [[['cart_total', total], ['cart_items', cart.item_count]]]);
  } catch (error) {
    mbCrispSigLog('cart.js read failed:', error.message);
  }

  // Hand the workflow what we just measured, not what Crisp has echoed back.
  await mbCrispSigFireCartAlert({ product, total, items: itemCount });
};

document.addEventListener('product-form:added', (event) => {
  mbCrispSigLog('product-form:added', event.detail?.variantId);
  mbCrispSigOnAdded(event.detail).catch((error) => mbCrispSigLog('signal failed:', error.message));
});

mbCrispSigIdentify().catch((error) => mbCrispSigLog('identify failed:', error.message));

